import { showConfirm } from '@/services/dialogService'

/**
 * Content kinds that are creator-only to edit/delete (see
 * docs/concepts/collaboration-and-permissions.md). Used only for dialog copy.
 */
export type CreatorOnlyItemKind = 'activity' | 'route' | 'track' | 'photo' | 'waypoint'

const KIND_LABEL: Record<CreatorOnlyItemKind, string> = {
  activity: 'activity',
  route: 'route',
  track: 'track',
  photo: 'photo',
  waypoint: 'waypoint',
}

/**
 * Shows the single-button "only the creator can edit this" explainer for unavailable actions that
 * remain discoverable. Intrinsic destructive exceptions such as whole-tour Delete are hidden by
 * their surfaces instead; this guard remains defense in depth for mutation entry points.
 */
export async function showCreatorOnlyDialog(
  kind: CreatorOnlyItemKind,
  createdByName?: string | null,
): Promise<void> {
  const who = createdByName ? createdByName : 'its creator'
  await showConfirm({
    title: 'Only the creator can edit this',
    message: `Only ${who} can edit or delete this ${KIND_LABEL[kind]}. You can still view it.`,
    confirmText: 'OK',
    variant: 'warning',
  })
}

/**
 * Gate for a creator-only mutation. `canEdit === false` means the server has already told us
 * this item belongs to someone else; show the explainer and refuse. `canEdit == null` (not yet
 * known, e.g. optimistic local state) defaults to allowed — the server remains authoritative and
 * will quarantine the event with a clear message if this guess was wrong.
 */
export async function requireItemEditable(
  canEdit: boolean | null | undefined,
  kind: CreatorOnlyItemKind,
  createdByName?: string | null,
): Promise<boolean> {
  if (canEdit !== false) return true
  await showCreatorOnlyDialog(kind, createdByName)
  return false
}
